export type ParsedDatalogName = {
  displayName: string;
  recordedAt: Date | null;
  rawName: string;
};

function safeDate(year: number, month: number, day: number, hour = 0, minute = 0, second = 0): Date | null {
  const date = new Date(year, month - 1, day, hour, minute, second);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day ||
    date.getHours() !== hour ||
    date.getMinutes() !== minute ||
    date.getSeconds() !== second
  ) {
    return null;
  }
  return date;
}

type DateMatch = {
  index: number;
  length: number;
  date: Date;
};

function findRecordedAt(baseName: string): DateMatch | null {
  const patterns: Array<{ regex: RegExp; build: (match: RegExpMatchArray) => Date | null }> = [
    {
      regex: /(\d{4})-(\d{2})-(\d{2})[T_ ](\d{2})[-:.]?(\d{2})(?:[-:.]?(\d{2}))?/g,
      build: (m) => safeDate(+m[1], +m[2], +m[3], +m[4], +m[5], +(m[6] || 0)),
    },
    {
      regex: /(\d{4})(\d{2})(\d{2})[_-](\d{2})(\d{2})(\d{2})/g,
      build: (m) => safeDate(+m[1], +m[2], +m[3], +m[4], +m[5], +m[6]),
    },
    {
      regex: /(\d{4})\.(\d{2})\.(\d{2})[_-](\d{2})\.(\d{2})(?:\.(\d{2}))?/g,
      build: (m) => safeDate(+m[1], +m[2], +m[3], +m[4], +m[5], +(m[6] || 0)),
    },
    {
      regex: /(?:^|[_\-.])(\d{4})(\d{2})(\d{2})(?![0-9])/g,
      build: (m) => safeDate(+m[1], +m[2], +m[3]),
    },
  ];

  let best: DateMatch | null = null;

  for (const { regex, build } of patterns) {
    for (const match of baseName.matchAll(regex)) {
      const date = build(match);
      if (!date || match.index === undefined) continue;

      const candidate: DateMatch = {
        index: match.index,
        length: match[0].length,
        date,
      };

      if (!best || candidate.date.getTime() > best.date.getTime()) {
        best = candidate;
      }
    }
  }

  return best;
}

function cleanLabel(value: string) {
  const cleaned = value.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();

  return cleaned || 'Datalog recording';
}

export function parseDatalogName(name: string): ParsedDatalogName {
  const rawName = name;
  const baseName = name.replace(/\.(csv|tsv)(\.gz)?$/i, '');
  const match = findRecordedAt(baseName);

  if (!match) {
    return {
      displayName: cleanLabel(baseName),
      recordedAt: null,
      rawName,
    };
  }

  const before = baseName.slice(0, match.index);
  const after = baseName.slice(match.index + match.length);
  const displayName = cleanLabel(`${before} ${after}`);

  return {
    displayName,
    recordedAt: match.date,
    rawName,
  };
}

export function formatRecordedAt(date: Date | null) {
  if (!date) return null;
  return date.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function getDatalogRecordDate(item: { name: string; updatedAt: string }) {
  const { recordedAt } = parseDatalogName(item.name);
  if (recordedAt) return recordedAt;
  const uploaded = new Date(item.updatedAt);
  return Number.isNaN(uploaded.getTime()) ? null : uploaded;
}

export function matchesDatalogSearch(
  item: { name: string; updatedAt: string },
  parsed: ParsedDatalogName,
  recordDate: Date | null,
  query: string,
) {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return true;

  const parts = [
    parsed.displayName,
    parsed.rawName,
    item.name,
    formatRecordedAt(parsed.recordedAt) ?? '',
    item.updatedAt,
  ];

  if (recordDate) {
    const year = recordDate.getFullYear();
    const month = String(recordDate.getMonth() + 1).padStart(2, '0');
    const day = String(recordDate.getDate()).padStart(2, '0');
    parts.push(
      `${year}-${month}-${day}`,
      `${year}/${month}/${day}`,
      `${day}/${month}/${year}`,
      `${year}${month}${day}`,
      recordDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }),
      recordDate.toLocaleDateString(undefined, { month: 'short', year: 'numeric' }),
      recordDate.toLocaleDateString(undefined, { month: 'long' }),
    );
  }

  return parts.some((part) => part.toLowerCase().includes(normalized));
}
